import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, chf } from "../lib/api";
import { useApp } from "../context/AppContext";
import SEO from "../components/SEO";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const { clearCart } = useApp();
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      attempts.current += 1;
      if (attempts.current > 12) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-5 text-center">
      <SEO title="Confirmation de commande" path="/payment/success" noindex />
      {status === "checking" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-terra" />
          <h1 data-testid="payment-checking" className="mt-6 font-serif text-4xl font-light text-ink">Confirmation du paiement…</h1>
          <p className="mt-2 text-ink2">Un instant, nous vérifions votre commande.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="h-12 w-12 text-sage" />
          <h1 data-testid="payment-success-title" className="mt-6 font-serif text-5xl font-light tracking-tight text-ink">Merci !</h1>
          <p data-testid="payment-success-message" className="mt-3 max-w-md text-ink2">
            Votre commande est confirmée{order?.total ? ` (${chf(order.total)})` : ""}. Je prépare vos pièces à la main
            et vous tiens informé par e-mail.
          </p>
          <Link to="/catalogue" className="mt-8 bg-ink px-8 py-4 text-sm uppercase tracking-widest text-cream transition-colors hover:bg-terra">
            Continuer
          </Link>
        </>
      )}
      {(status === "failed" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="h-12 w-12 text-terra" />
          <h1 data-testid="payment-failed-title" className="mt-6 font-serif text-4xl font-light text-ink">Paiement non confirmé</h1>
          <p className="mt-2 text-ink2">
            {status === "timeout" ? "La confirmation prend plus de temps que prévu." : "Le paiement n'a pas abouti."} Vous pouvez réessayer.
          </p>
          <Link to="/panier" className="mt-8 border border-ink px-8 py-4 text-sm uppercase tracking-widest text-ink transition-colors hover:bg-ink hover:text-cream">
            Retour au panier
          </Link>
        </>
      )}
    </div>
  );
}
